// --------------------------------------------------------
// 1. Core Base Types (ประเภทข้อมูลพื้นฐานที่ใช้ร่วมกัน)
// --------------------------------------------------------

// Mapping ระหว่าง Group ID กับ Key ใน JSON
export const SCOPE_MAPPING: Record<string, keyof ScopeBreakdown | "s0"> = {
  "Scope-1": "s1",
  "Scope-2": "s2",
  "Scope-3": "s3",
  "Outside-of-Scope": "s0",
};

export interface EmissionValue {
  value: number;
  percent: number;
}

export interface ScopeBreakdown {
  s0: EmissionValue;
  s1: EmissionValue;
  s2: EmissionValue;
  s3: EmissionValue;
}

export interface EmissionSource {
  categoryEn: string;
  categoryTH: string;
  value: number;
  percent: number;
}

export interface ScopeSummary extends ScopeBreakdown {
  total: number;
}

// --------------------------------------------------------
// 2. Item Details (Asset, Source, Category)
// --------------------------------------------------------

export interface AssetData extends EmissionValue {
  asset: string;
}

export interface SourceData extends EmissionValue {
  categoryEn: string;
  categoryTH: string;
}

export interface CategoryTypeData extends EmissionSource {}

// --------------------------------------------------------
// 3. Business Structure Types
// --------------------------------------------------------

export interface BusinessEntityDetail extends EmissionValue {
  name: string;
  subOrg?: string;
  types: CategoryTypeData[];
}

export interface BusinessEntitySummary {
  name: string;
  subOrg?: string;
  percent: number;
  summary: ScopeSummary;
}

export interface BusinessStructureDetailGroup {
  subOrganizations: BusinessEntityDetail[];
  sites: BusinessEntityDetail[];
}

export interface BusinessStructureSummaryGroup {
  subOrganizations: BusinessEntitySummary[];
  sites: BusinessEntitySummary[];
}

// --------------------------------------------------------
// 4. Time Based Data (Monthly / Yearly)
// --------------------------------------------------------

export interface YearlyData extends ScopeSummary {
  year: number;
}

export interface PreviousYearData extends ScopeBreakdown {
  year: number;
  month: number;
  total: number;
  yoy_percent: number | null;
  types?: EmissionSource[]; // Optional ในบางจุด
}

export interface MonthlyData extends ScopeBreakdown {
  year: number;
  month: number;
  total: number;
  yoy_percent: number | null;
  previous_1year: PreviousYearData;
  previous_2year: PreviousYearData;
  types?: EmissionSource[]; // Optional เพราะบางที Monthly ใหญ่ไม่มี types
}

// --------------------------------------------------------
// 5. Scope Specific Details (รายละเอียดภายใน byScopes)
// --------------------------------------------------------

export interface ScopeDetailData {
  total: number;
  sumScopes: number;
  percent: number;
  byTypes: {
    total: number;
    types: CategoryTypeData[];
  };
  // ✅ เพิ่มตรงนี้ครับ: ข้อมูลรายเดือนเฉพาะของ Scope นั้น
  monthly: MonthlyData[] | null;
  byAssets: AssetData[];
  bySources: SourceData[];
  byBusinessStructure: BusinessStructureDetailGroup;
}

export interface ByScopesGroup {
  s0: ScopeDetailData;
  s1: ScopeDetailData;
  s2: ScopeDetailData;
  s3: ScopeDetailData;
}

// --------------------------------------------------------
// 6. Chart / Table Row (UI layer types)
// --------------------------------------------------------

export interface EmissionTableRow {
  label: string;
  fullLabel: string;
  total: number;
  scopes: Partial<ScopeBreakdown>;
  previousTotal: number;
  yoy: number | null;
}

// --------------------------------------------------------
// 7. Main Response Interface
// --------------------------------------------------------

export interface DashboardResults {
  sumScopes: ScopeSummary;
  byAssets: AssetData[];
  bySources: SourceData[];
  byScopes: ByScopesGroup;
  byTypes: {
    s0: { total: number; types: CategoryTypeData[] };
    s1: { total: number; types: CategoryTypeData[] };
    s2: { total: number; types: CategoryTypeData[] };
    s3: { total: number; types: CategoryTypeData[] };
  };
  byBusinessStructure: BusinessStructureSummaryGroup;
  monthly: MonthlyData[] | null;
  yearly: YearlyData[] | null;
}
